import Phaser from 'phaser';
import { COLORS } from './theme';

export type NavIconType = 'menu' | 'kitchen' | 'oven' | 'shop';

export interface NavIconOptions {
  type: NavIconType;
  size?: number;
  onClick?: () => void;
}

// Graphics-drawn nav icons matching the Scratch original silhouettes:
// menu = recipe book, kitchen = stand mixer, oven = oven box, shop = storefront.
// All are sharp at any scale, themed from the COLORS palette.
export class NavIcon extends Phaser.GameObjects.Container {
  readonly iconType: NavIconType;
  private readonly art: Phaser.GameObjects.Graphics;
  private readonly hitW: number;
  private readonly hitH: number;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: NavIconOptions) {
    super(scene, x, y);
    scene.add.existing(this);
    this.iconType = opts.type;

    const size = opts.size ?? 48;
    this.hitW = size + 12;
    this.hitH = size + 12;

    this.art = scene.add.graphics();
    this.add(this.art);
    this.draw(size);

    this.setInteractive(
      new Phaser.Geom.Rectangle(-this.hitW / 2, -this.hitH / 2, this.hitW, this.hitH),
      Phaser.Geom.Rectangle.Contains,
    );
    this.input!.cursor = 'pointer';

    this.on('pointerover', () => this.setScale(1.08));
    this.on('pointerout',  () => this.setScale(1));
    this.on('pointerdown', () => this.setScale(0.96));
    this.on('pointerup',   () => {
      this.setScale(1.08);
      opts.onClick?.();
    });
  }

  private draw(size: number): void {
    const g = this.art;
    const s = size / 2;
    const ink = COLORS.headerPurpleDeep;
    const paper = 0xfff7ec;
    const orange = COLORS.accentOrange;

    switch (this.iconType) {
      case 'menu': {
        // Recipe book / clipboard: rounded page with horizontal text lines.
        g.fillStyle(paper, 1);
        g.fillRoundedRect(-s * 0.78, -s * 0.95, s * 1.56, s * 1.9, s * 0.16);
        g.lineStyle(s * 0.10, ink, 1);
        g.strokeRoundedRect(-s * 0.78, -s * 0.95, s * 1.56, s * 1.9, s * 0.16);
        // Clip top
        g.fillStyle(ink, 1);
        g.fillRoundedRect(-s * 0.30, -s * 1.05, s * 0.6, s * 0.30, s * 0.08);
        // Lines
        g.lineStyle(s * 0.08, ink, 1);
        for (let i = 0; i < 4; i++) {
          const y = -s * 0.45 + i * (s * 0.34);
          g.lineBetween(-s * 0.50, y, s * 0.50, y);
        }
        break;
      }
      case 'kitchen': {
        // KitchenAid-style stand mixer profile:
        // base block, vertical post, motor housing leaning over a bowl,
        // beater shaft descending from housing into the bowl.

        // Bowl (foreground, drawn first so post overlaps it slightly).
        g.fillStyle(paper, 1);
        g.fillRoundedRect(-s * 0.40, s * 0.30, s * 1.10, s * 0.55, s * 0.20);
        g.lineStyle(s * 0.10, ink, 1);
        g.strokeRoundedRect(-s * 0.40, s * 0.30, s * 1.10, s * 0.55, s * 0.20);
        g.lineStyle(0, 0, 0);

        // Vertical post + base (back of mixer).
        g.fillStyle(orange, 1);
        // Base block
        g.fillRoundedRect(-s * 0.86, s * 0.62, s * 0.46, s * 0.30, s * 0.07);
        // Post rising from base
        g.fillRect(-s * 0.72, -s * 0.42, s * 0.20, s * 1.10);

        // Motor housing — slanting forward, sitting on top of post.
        // Drawn as a closed polygon for the characteristic teardrop silhouette.
        g.beginPath();
        g.moveTo(-s * 0.78, -s * 0.42);
        g.lineTo(-s * 0.78, -s * 0.78);
        g.lineTo( s * 0.20, -s * 0.92);
        g.lineTo( s * 0.62, -s * 0.50);
        g.lineTo( s * 0.62, -s * 0.20);
        g.lineTo(-s * 0.50, -s * 0.10);
        g.closePath();
        g.fillPath();

        // Ink outline of housing for definition.
        g.lineStyle(s * 0.07, ink, 1);
        g.beginPath();
        g.moveTo(-s * 0.78, -s * 0.42);
        g.lineTo(-s * 0.78, -s * 0.78);
        g.lineTo( s * 0.20, -s * 0.92);
        g.lineTo( s * 0.62, -s * 0.50);
        g.lineTo( s * 0.62, -s * 0.20);
        g.lineTo(-s * 0.50, -s * 0.10);
        g.closePath();
        g.strokePath();
        g.lineStyle(0, 0, 0);

        // Beater shaft + whisk descending from motor housing into the bowl.
        g.fillStyle(ink, 1);
        g.fillRect(s * 0.20, -s * 0.20, s * 0.10, s * 0.40);
        // Whisk wedge
        g.fillTriangle(s * 0.04, s * 0.20, s * 0.46, s * 0.20, s * 0.25, s * 0.58);
        break;
      }
      case 'oven': {
        // Oven box with door window and small knobs across the top.
        g.fillStyle(paper, 1);
        g.fillRoundedRect(-s * 0.90, -s * 0.85, s * 1.80, s * 1.80, s * 0.16);
        g.lineStyle(s * 0.10, ink, 1);
        g.strokeRoundedRect(-s * 0.90, -s * 0.85, s * 1.80, s * 1.80, s * 0.16);
        // Knobs row
        g.fillStyle(ink, 1);
        for (let i = -1; i <= 1; i++) g.fillCircle(i * s * 0.42, -s * 0.55, s * 0.08);
        // Door window
        g.fillStyle(ink, 0.85);
        g.fillRoundedRect(-s * 0.62, -s * 0.20, s * 1.24, s * 1.00, s * 0.10);
        // Handle below window
        g.fillStyle(ink, 1);
        g.fillRoundedRect(-s * 0.62, s * 0.86, s * 1.24, s * 0.12, s * 0.06);
        break;
      }
      case 'shop': {
        // Storefront: awning + display case + cupcake.
        // Awning
        g.fillStyle(COLORS.accentPink, 1);
        g.fillRoundedRect(-s * 0.95, -s * 0.95, s * 1.90, s * 0.36, s * 0.10);
        g.lineStyle(s * 0.06, ink, 1);
        g.strokeRoundedRect(-s * 0.95, -s * 0.95, s * 1.90, s * 0.36, s * 0.10);
        // Storefront body
        g.fillStyle(paper, 1);
        g.fillRect(-s * 0.85, -s * 0.59, s * 1.70, s * 1.50);
        g.lineStyle(s * 0.08, ink, 1);
        g.strokeRect(-s * 0.85, -s * 0.59, s * 1.70, s * 1.50);
        // Cupcake on display
        const cx = 0, cy = s * 0.10;
        // wrapper
        g.fillStyle(orange, 1);
        g.beginPath();
        g.moveTo(cx - s * 0.32, cy);
        g.lineTo(cx + s * 0.32, cy);
        g.lineTo(cx + s * 0.24, cy + s * 0.42);
        g.lineTo(cx - s * 0.24, cy + s * 0.42);
        g.closePath();
        g.fillPath();
        // frosting
        g.fillStyle(COLORS.accentPink, 1);
        g.fillCircle(cx, cy - s * 0.04, s * 0.30);
        // cherry
        g.fillStyle(0xd4322a, 1);
        g.fillCircle(cx, cy - s * 0.30, s * 0.10);
        break;
      }
    }
  }
}
